import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Globe } from "lucide-react";

// Fetches available states/locations from backend so the list stays in sync.
export function useStates() {
  const [config, setConfig] = useState({ states: ["MA", "NY", "NYT", "CAN"], generic: "GEN" });
  useEffect(() => {
    let cancelled = false;
    api.get("/config/states").then((r) => { if (!cancelled) setConfig(r.data); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  return config;
}

/**
 * StateSelector — pill-checkbox UI for selecting the locations an asset belongs to.
 * Value is a comma-separated string (matches backend upload endpoint), OR an array.
 * `value` — string ("MA,GEN") or array (["MA","GEN"])
 * `onChange(nextString, nextArray)` — receives both formats
 */
export default function StateSelector({ value = "", onChange, testIdPrefix = "state" }) {
  const { states, generic } = useStates();
  const options = [generic, ...states];

  const parsed = Array.isArray(value)
    ? value
    : String(value || "").split(",").map((s) => s.trim()).filter(Boolean);

  function toggle(code) {
    const set = new Set(parsed);
    if (set.has(code)) set.delete(code);
    else set.add(code);
    const arr = Array.from(set);
    const str = arr.join(", ");
    onChange && onChange(str, arr);
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Location Access</label>
        <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">{parsed.length === 0 ? "None (admin only)" : `${parsed.length} selected`}</span>
      </div>
      <div className="flex flex-wrap gap-2" data-testid={`${testIdPrefix}-selector`}>
        {options.map((code) => {
          const isGen = code === generic;
          const active = parsed.includes(code);
          return (
            <button
              key={code}
              type="button"
              onClick={() => toggle(code)}
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-[0.2em] border rounded-sm transition flex items-center gap-1.5 ${
                active
                  ? isGen ? "bg-foreground text-background border-foreground" : "bg-foreground text-background border-foreground"
                  : "border-border hover:border-border-strong text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`${testIdPrefix}-${code}`}
            >
              {isGen && <Globe className="h-3 w-3" />}
              {code}
            </button>
          );
        })}
      </div>
      {parsed.includes(generic) && (
        <p className="mt-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Generic — visible to every partner regardless of location</p>
      )}
    </div>
  );
}
